"use client";

// HoverField — THE hover tracker (law_list_language v2 · law_nav_proximity,
// 2026-09-23). Mounted once in the root layout, so every surface (dashboard,
// docs, company, community, admin, rooms) answers the pointer the same way.
//
// It owns one piece of state: `data-hl` on the thing under a mouse or pen
// (any HL_SELECTOR match, plus each lit ancestor, the way :hover nests), with
// --mx/--my written on it for the glow. The CSS does the rest: the wash, the
// ink uptake, the glow as icing (globals.css, "THE HOVER LANGUAGE").
//
// Why a tracker and not :hover. :hover sticks: on touch it stays after a tap;
// on a still pointer it is not re-checked while the page scrolls (Safari never,
// Chrome late) or while a row glides away under it (the navigator strip, the
// directory tiles, a remount); framer's whileHover and React's hover state only
// clear on a pointerleave that never fires in those cases. So this tracker
// CLEARS at once on scroll, wheel, pointercancel, a touch, the pointer leaving
// the window, blur and a hidden tab, and RE-HIT-TESTS the still pointer after
// the scroll settles, after anything glides (transform/left/top transitionend)
// and on a slow idle pulse while something is lit, so what is lit is always
// what is under the pointer.
//
// Proximity zones: an element with `data-hl-near="<px>"` gets `data-near`
// while the pointer is within that many px above/below it (12px sideways). The
// navigator's rail uses it so its words come up to meet the hand.
//
// Performance: ONE passive capture listener per event type on the document,
// every pointermove coalesced into one requestAnimationFrame pass, and each
// pass does all its layout reads (the lit chain's rects, the zones' rects)
// before any write. No per-element listeners, nothing per frame when idle.

import { useEffect } from "react";
import {
  HL_ATTR,
  HL_JS_ATTR,
  HL_MAX_CHAIN,
  HL_NEAR_ATTR,
  HL_NEAR_SELECTOR,
  HL_SELECTOR,
  NEAR_ATTR,
  glowPoint,
  isDrag,
  movesLayout,
  nearHit,
  nearPad,
} from "../lib/os/hover";

const IDLE_PULSE_MS = 250; // re-hit-test cadence while something is lit
const IDLE_TAIL_MS = 2000; // …and for this long after the last move otherwise
const SCROLL_SETTLE_MS = 120; // scrollend fallback (Safari) + wheel settle
const ZONES_TTL_MS = 500; // how long the zone list is trusted between queries

export default function HoverField() {
  useEffect(() => {
    const doc = document;
    const root = doc.documentElement;
    root.setAttribute(HL_JS_ATTR, "");

    let lit: HTMLElement[] = []; // innermost first
    let near: HTMLElement[] = [];
    let zones: HTMLElement[] = [];
    let zonesAt = -Infinity;
    const written = new WeakMap<HTMLElement, string>();

    let x = 0;
    let y = 0;
    let has = false; // a mouse/pen pointer is known to be over the page
    let suspended = false; // a scroll/wheel is in flight
    let captured = false; // a press holds pointer capture (frozen, not cleared)
    let capX = 0; // where that press began
    let capY = 0;
    let target: Element | null = null; // the last event target, when fresh
    let under: Element | null = null; // what the last pass found under the pointer
    let rehit = false; // hit-test the point instead of trusting `target`
    let lastMove = 0;
    let raf = 0;
    let pulse = 0;
    let settle = 0;

    const chainOf = (el: Element | null): HTMLElement[] => {
      const out: HTMLElement[] = [];
      let t = el && typeof el.closest === "function" ? (el.closest(HL_SELECTOR) as HTMLElement | null) : null;
      while (t && out.length < HL_MAX_CHAIN) {
        out.push(t);
        t = (t.parentElement?.closest(HL_SELECTOR) as HTMLElement | null) ?? null;
      }
      return out;
    };

    const setLit = (next: HTMLElement[]) => {
      for (const el of lit) if (!next.includes(el)) el.removeAttribute(HL_ATTR);
      for (const el of next) if (!lit.includes(el)) el.setAttribute(HL_ATTR, "");
      lit = next;
    };
    const setNear = (next: HTMLElement[]) => {
      for (const el of near) if (!next.includes(el)) el.removeAttribute(NEAR_ATTR);
      for (const el of next) if (!near.includes(el)) el.setAttribute(NEAR_ATTR, "");
      near = next;
    };
    const clear = () => {
      setLit([]);
      setNear([]);
    };

    const frame = () => {
      raf = 0;
      if (!has || suspended || captured || doc.hidden) return;
      let hit = target;
      target = null;
      if (rehit || !hit || !hit.isConnected) hit = doc.elementFromPoint(x, y);
      rehit = false;
      under = hit;
      const next = chainOf(hit);

      const now = performance.now();
      if (now - zonesAt > ZONES_TTL_MS || zones.some((z) => !z.isConnected)) {
        zones = Array.from(doc.querySelectorAll<HTMLElement>(HL_NEAR_SELECTOR));
        zonesAt = now;
      }

      // ── reads ──
      const rects = next.map((el) => el.getBoundingClientRect());
      const zoneRects = zones.map((z) => z.getBoundingClientRect());

      // ── writes ──
      setLit(next);
      for (let i = 0; i < next.length; i++) {
        const el = next[i];
        const { mx, my } = glowPoint(rects[i], x, y);
        const key = `${mx} ${my}`;
        if (written.get(el) === key) continue;
        written.set(el, key);
        el.style.setProperty("--mx", mx);
        el.style.setProperty("--my", my);
      }
      setNear(zones.filter((z, i) => nearHit(zoneRects[i], x, y, nearPad(z.getAttribute(HL_NEAR_ATTR)))));
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };
    const tick = () => {
      pulse = 0;
      if (!has || doc.hidden) return;
      if (!suspended && !captured) {
        rehit = true;
        schedule();
      }
      if (lit.length || near.length || performance.now() - lastMove < IDLE_TAIL_MS) {
        pulse = window.setTimeout(tick, IDLE_PULSE_MS);
      }
    };
    const startPulse = () => {
      if (!pulse) pulse = window.setTimeout(tick, IDLE_PULSE_MS);
    };
    const resume = () => {
      settle = 0;
      suspended = false;
      rehit = true;
      schedule();
      startPulse();
    };
    const suspend = () => {
      clear();
      suspended = true;
      if (settle) clearTimeout(settle);
      settle = window.setTimeout(resume, SCROLL_SETTLE_MS);
    };
    const leave = () => {
      has = false;
      target = null;
      under = null;
      clear();
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" && e.pointerType !== "pen") return;
      x = e.clientX;
      y = e.clientY;
      // a captured press keeps what it lit; once it travels it is a real drag
      // (the thing under it is moving with the hand), and the light lets go.
      if (captured) {
        if ((lit.length || near.length) && isDrag(x - capX, y - capY)) clear();
        return;
      }
      has = true;
      lastMove = performance.now();
      target = e.target instanceof Element ? e.target : null;
      schedule();
      startPulse();
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") leave();
    };
    const onOut = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!e.relatedTarget) leave(); // left the window (or into an iframe)
    };
    const onScroll = (e: Event) => {
      if (!has) return;
      const t = e.target;
      // only a scroll that moves what is under the pointer: the page itself, or
      // a scroller holding something lit / the last thing the pointer was over.
      const moved =
        t === doc ||
        t === root ||
        t === doc.body ||
        !(t instanceof Node) ||
        lit.some((el) => t.contains(el)) ||
        near.some((el) => t.contains(el)) ||
        (under instanceof Node && t.contains(under));
      if (moved) suspend();
    };
    const onScrollEnd = () => {
      if (!suspended) return;
      if (settle) clearTimeout(settle);
      resume();
    };
    const onWheel = () => {
      if (has && (lit.length || near.length || suspended)) suspend();
    };
    const onTransitionEnd = (e: TransitionEvent) => {
      if (!has || suspended || !movesLayout(e.propertyName)) return;
      rehit = true;
      schedule();
    };
    const onGotCapture = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      // a PRESS takes capture too (use-gesture's useDrag on every tile): keep
      // the lift and the lit name frozen under the finger. frame() rests while
      // captured; a pointermove past the slop is what clears (onMove).
      captured = true;
      capX = e.clientX || x;
      capY = e.clientY || y;
    };
    const onLostCapture = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      captured = false;
      clear();
      rehit = true;
      schedule();
    };
    const onVisibility = () => {
      if (doc.hidden) leave();
    };

    const cap = { capture: true, passive: true } as const;
    doc.addEventListener("pointermove", onMove, cap);
    doc.addEventListener("pointerdown", onDown, cap);
    doc.addEventListener("pointerout", onOut, cap);
    doc.addEventListener("pointercancel", leave, cap);
    doc.addEventListener("gotpointercapture", onGotCapture, cap);
    doc.addEventListener("lostpointercapture", onLostCapture, cap);
    doc.addEventListener("scroll", onScroll, cap);
    doc.addEventListener("scrollend", onScrollEnd, cap);
    doc.addEventListener("wheel", onWheel, cap);
    doc.addEventListener("transitionend", onTransitionEnd, cap);
    doc.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", leave);

    return () => {
      doc.removeEventListener("pointermove", onMove, cap);
      doc.removeEventListener("pointerdown", onDown, cap);
      doc.removeEventListener("pointerout", onOut, cap);
      doc.removeEventListener("pointercancel", leave, cap);
      doc.removeEventListener("gotpointercapture", onGotCapture, cap);
      doc.removeEventListener("lostpointercapture", onLostCapture, cap);
      doc.removeEventListener("scroll", onScroll, cap);
      doc.removeEventListener("scrollend", onScrollEnd, cap);
      doc.removeEventListener("wheel", onWheel, cap);
      doc.removeEventListener("transitionend", onTransitionEnd, cap);
      doc.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", leave);
      cancelAnimationFrame(raf);
      clearTimeout(pulse);
      clearTimeout(settle);
      clear();
      root.removeAttribute(HL_JS_ATTR);
    };
  }, []);

  return null;
}
